import { useState, useEffect } from 'react';
import {
  Table, Button, Tag, Modal, Form, Input, InputNumber, ColorPicker, Switch,
  Popconfirm, App, Empty, Spin,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Plus, Edit2, Trash2, FolderArchive } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import {
  orgService,
  type DepartmentGroup,
  type CreateDepartmentGroupRequest,
} from '@/services/rolesService';

/**
 * V1.0.32: Trang quản lý Nhóm khoa phòng standalone.
 *
 * Route: /categories/dept-groups
 *
 * Cho phép admin CRUD nhóm khoa phòng. Nhóm này dùng để gom các khoa
 * khi hiển thị trên menu Tài liệu, Dự án... (toggle "Gom theo nhóm").
 */
export function DeptGroupsPage() {
  const { message } = App.useApp();
  const [data, setData] = useState<DepartmentGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<DepartmentGroup | null>(null);
  const [openCreate, setOpenCreate] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const list = await orgService.listGroupsAdmin();
      setData(list);
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const handleDelete = async (g: DepartmentGroup) => {
    try {
      await orgService.deleteGroup(g.id);
      message.success(`Đã xóa nhóm "${g.name}". Các khoa thuộc nhóm sẽ chuyển sang Chưa phân nhóm.`);
      refresh();
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message);
    }
  };

  const columns: ColumnsType<DepartmentGroup> = [
    {
      title: 'Màu', width: 60, align: 'center',
      render: (_, r) => (
        <span style={{
          display: 'inline-block', width: 22, height: 22, borderRadius: '50%',
          background: r.color, border: '1px solid #e8edf2',
        }} />
      ),
    },
    {
      title: 'Mã nhóm', dataIndex: 'code', width: 140,
      render: (v) => <span className="mono" style={{ fontWeight: 600, color: '#1976D2' }}>{v}</span>,
    },
    {
      title: 'Tên nhóm', dataIndex: 'name',
      render: (v) => <span style={{ fontWeight: 500 }}>{v}</span>,
    },
    {
      title: 'Mô tả', dataIndex: 'description',
      render: (v) => v || <span style={{ color: '#adb6c0' }}>—</span>,
    },
    {
      title: 'Số khoa', dataIndex: 'departmentCount', width: 100, align: 'center',
      render: (v) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: 'Thứ tự', dataIndex: 'sortOrder', width: 90, align: 'center',
    },
    {
      title: 'Hoạt động', dataIndex: 'isActive', width: 110, align: 'center',
      render: (v) => v ? <Tag color="success">● Bật</Tag> : <Tag>○ Tắt</Tag>,
    },
    {
      title: 'Thao tác', width: 180, fixed: 'right',
      render: (_, r) => (
        <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
          <Button size="small" icon={<Edit2 size={12} />} onClick={() => setEditing(r)}>
            Sửa
          </Button>
          <Popconfirm
            title="Xóa nhóm?"
            description={r.departmentCount > 0
              ? `Có ${r.departmentCount} khoa đang thuộc nhóm này. Sau khi xóa, các khoa sẽ chuyển sang "Chưa phân nhóm".`
              : 'Nhóm này chưa có khoa nào.'}
            onConfirm={() => handleDelete(r)}
            okText="Xóa" cancelText="Hủy" okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<Trash2 size={12} />}>Xóa</Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        breadcrumb="DANH MỤC / NHÓM KHOA PHÒNG"
        title="Nhóm khoa phòng"
        subtitle="Phân loại khoa phòng theo nhóm (vd: Lâm sàng, Cận lâm sàng, Hỗ trợ). Áp dụng để gom hiển thị trên menu Tài liệu và Dự án."
        extra={
          <Button type="primary" icon={<Plus size={14} />} onClick={() => setOpenCreate(true)}>
            Thêm nhóm
          </Button>
        }
      />
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>
      ) : data.length === 0 ? (
        <Empty
          image={<FolderArchive size={48} style={{ color: '#bdc4cd' }} />}
          description={
            <div>
              <div style={{ marginBottom: 12 }}>Chưa có nhóm nào.</div>
              <div style={{ fontSize: 12, color: '#7d8a98' }}>
                Tạo nhóm đầu tiên để bắt đầu phân loại khoa phòng.
              </div>
            </div>
          }
        >
          <Button type="primary" icon={<Plus size={14} />} onClick={() => setOpenCreate(true)}>
            Tạo nhóm đầu tiên
          </Button>
        </Empty>
      ) : (
        <Table
          rowKey="id"
          dataSource={data}
          columns={columns}
          pagination={false}
          size="middle"
        />
      )}

      {openCreate && (
        <GroupFormModal
          onClose={() => setOpenCreate(false)}
          onSaved={() => { setOpenCreate(false); refresh(); }}
        />
      )}
      {editing && (
        <GroupFormModal
          group={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      )}
    </>
  );
}

function GroupFormModal({ group, onClose, onSaved }: {
  group?: DepartmentGroup;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const isEdit = !!group;

  useEffect(() => {
    if (group) {
      form.setFieldsValue({
        code: group.code,
        name: group.name,
        description: group.description ?? '',
        color: group.color,
        sortOrder: group.sortOrder,
        isActive: group.isActive,
      });
    } else {
      form.setFieldsValue({ color: '#1976D2', sortOrder: 100, isActive: true });
    }
  }, [group]);

  const submit = async () => {
    const values = await form.validateFields();
    setSaving(true);
    try {
      const color = typeof values.color === 'string'
        ? values.color
        : values.color?.toHexString?.() ?? '#1976D2';
      const payload: CreateDepartmentGroupRequest = {
        code: values.code,
        name: values.name,
        description: values.description || undefined,
        color,
        sortOrder: values.sortOrder,
      };
      if (isEdit && group) {
        await orgService.updateGroup(group.id, { ...payload, isActive: values.isActive });
        message.success('Đã cập nhật nhóm');
      } else {
        await orgService.createGroup(payload);
        message.success('Đã tạo nhóm mới');
      }
      onSaved();
    } catch (e: any) {
      message.error(e.response?.data?.message ?? e.message);
    } finally { setSaving(false); }
  };

  return (
    <Modal
      title={isEdit ? `Sửa nhóm: ${group?.name}` : 'Tạo nhóm khoa phòng mới'}
      open
      onCancel={onClose}
      onOk={submit}
      confirmLoading={saving}
      okText={isEdit ? 'Lưu' : 'Tạo'}
      cancelText="Hủy"
      width={560}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="code"
          label="Mã nhóm"
          rules={[
            { required: true, message: 'Bắt buộc' },
            { pattern: /^[A-Z0-9_-]+$/, message: 'Chỉ A-Z, 0-9, _, -' },
          ]}
          extra="VD: LAMSANG, CANLAMSANG, HOTRO"
        >
          <Input placeholder="LAMSANG" disabled={isEdit} />
        </Form.Item>
        <Form.Item
          name="name"
          label="Tên nhóm"
          rules={[{ required: true, message: 'Bắt buộc' }]}
        >
          <Input placeholder="Khoa Lâm sàng" />
        </Form.Item>
        <Form.Item name="description" label="Mô tả">
          <Input.TextArea rows={2} placeholder="(tùy chọn)" />
        </Form.Item>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Form.Item name="color" label="Màu nhãn">
            <ColorPicker showText />
          </Form.Item>
          <Form.Item name="sortOrder" label="Thứ tự sắp xếp" extra="Nhỏ → hiển thị trước">
            <InputNumber min={0} max={9999} style={{ width: '100%' }} />
          </Form.Item>
        </div>
        {isEdit && (
          <Form.Item name="isActive" label="Hoạt động" valuePropName="checked">
            <Switch />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}
